import axios, { AxiosRequestConfig } from "axios";
import { ApiError } from "./ApiError";
import { EquipmentCancelConf, ObjectToEquipmentCancelConf } from "../Objects/EquipmentCancelConf";
import { ObjectToStudioCancelConf, StudioCancelConf } from "../Objects/StudioCancelConf";

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRF-Token';

export interface SystemValueInterface {
    AUTO_CASH_REGISTER: string, // ex) "ENABLE"
    COUPON_BLOCK_EXPIRED: string, // ex) "ENABLE"
    COUPON_EQUIPMENT_CHARGE_ATOMICITY: string, // ex) "100%"
    COUPON_EQUIPMENT_CHARGE_RAITO: string, // ex) "0"
    COUPON_MEMBERSHIP_REGISTER: string, // ex) "0"
    COUPON_MEMBERSHIP_REISSUE: string, // ex) "0"
    COUPON_MEMBERSHIP_UPDATE: string, // ex) "500"
    COUPON_RESET_TIMING: string, // ex) "UPDATE"
    COUPON_STUDIO_CHARGE_ATOMICITY: string, // ex) "1000x"
    COUPON_STUDIO_CHARGE_RAITO: string, // ex) "1"
    COUPON_VISIT_POINT: string, // ex) "10"
    DATABASE_VERSION: string, // ex) "10"
    ENABLE_MEMBER_SYSTEM: string, // ex) "ENABLE"
    MAIL_LOG_SENDER: string, // ex) "xxx@yyyy.jp"
    MAIL_SEND_CONFIRM: string, // ex) "ENABLE"
    MEMBERSHIP_CODE_UPPER: string, // ex) "1283739"
    MEMBERSHIP_LIMIT: string, // ex) "2 year"
    MEMBERSHIP_REG_CASHITEM: string, // ex) "1"
    MEMBERSHIP_REG_CHARGE: string, // ex) "1000"
    MEMBERSHIP_REISSUE_CASHITEM: string, // ex) "3"
    MEMBERSHIP_REISSUE_CHARGE: string, // ex) "500"
    MEMBERSHIP_UPDATE_CASHITEM: string, // ex) "2"
    MEMBERSHIP_UPDATE_CHARGE: string, // ex) "1000"
    MEMBERSHIP_UPDATE_LIMIT: string, // ex) "-1 year"
    RESERVE_ENABLE_SPAN: string, // ex) "2 MONTHEND"
    RESERVE_ENABLE_SPAN_STORE_SYSTEM: string, // ex) "PROHIBIT"
    RESERVE_PENDING_TIMEOUT: string, // ex) "15 MINUTE"
    STORE_VISITORFLUG_SETTING_ENABLE: string, // ex) "DISABLE"
    SYSTEM_BASE_URL: string, // ex) "http://rv.blstudio.jp/"
    SYSTEM_CONTACT_MAIL: string, // ex) "std@blstudio.jp"
    SYSTEM_LOGO_IMGDATA: string, // ex) "PD94bWwgdmVyc2lvbj0iMS...
    SYSTEM_LOGO_MIME_TYPE: string, // ex) "image/svg+xml"
    SYSTEM_MAIL_SENDER: string, // ex) "aaa@yyy.jp"
    SYSTEM_MYPAGE_TITLE: string, // ex) "mypage title"
    SYSTEM_MYPAGE_URL: string, // ex) "http://yyyy.jp/"
    SYSTEM_REGISTER_MAIL: string, // ex) "register_mail@yyyy.jp"
    TEXT_CANCEL_INFO: string, // ex) "・予約開始..."
    TEXT_MYPAGE_RESERVE_CATEGORY: string, // ex) 
    ENABLE_ONLINE_LESSON: string, // ex) "ENABLE"
}

/**
 * getSystemValue
 * @returns string[]
 */
export const getSystemValue = async ({
    site
}: {
    site: string
}, config?: AxiosRequestConfig): Promise<SystemValueInterface> => {
    try {
        let params = new URLSearchParams()
        params.append('site', site)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/get_system_value',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return res.data.data
    } catch (error) {
        throw error
    }
}

/**
 * setSystemValue
 */
export const setSystemValue = async ({
    key,
    value
}: {
    key: string,
    value: string
}, config?: AxiosRequestConfig): Promise<void> => {
    try {
        if( key === "SYSTEM_LOGO_IMGDATA" ) {
            return uploadLogoImage({
                image: value
            })
        }
        let params = new URLSearchParams()
        params.append('field', key)
        params.append('value', value)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/set_system_value',
            params, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
    } catch (error) {
        throw error
    }
}

export const uploadLogoImage = async ({
    image
}: {
    image: string
}, config?: AxiosRequestConfig): Promise<void> => {
    try {
        const blob = await (await fetch(image)).blob()
        const file = new File([blob], "logo", {type: blob.type})
        const data = new FormData()
        data.append("logoimg", file)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/upload_logo_image',
            data, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }

    } catch (error) {
        
    }
}

export interface DatabaseVersion {
    latest_version: number,
    current_version: number
}
/**
 * checkDatabaseVersion
 * @returns DatabaseVersion
 */
export const checkDatabaseVersion = async (config?: AxiosRequestConfig): Promise<DatabaseVersion> => {
    try {
        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/check_database_version', undefined, config
        )
        if (res.data.status !== 'SUCCESS') {
            throw new ApiError("サーバーエラー", res.data.status)
        }
        return {
            latest_version: res.data.latest_version,
            current_version: res.data.current_version
        }
    } catch (error) {
        throw error
    }
}

export interface UpdateDatabaseProps {
    current_version: number,
    update_version: number
}
/**
 * updateDatabase
 * @param UpdateDatabaseProps 
 */
export const updateDatabase = async({
    current_version,
    update_version
}: UpdateDatabaseProps, config?: AxiosRequestConfig): Promise<void> => {
    try {
        let params = new URLSearchParams()
        params.append('current_version', current_version.toString())
        params.append('update_version', update_version.toString())

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/update_database',
            params, config
        )
        switch (res.data.status) {
            case "SUCCESS":
                break
        
            case "DATABASE_BROKEN":
                throw new ApiError("不明のエラーによりデータベースの修復ができませんでした。サポートへ連絡お願いします。", res.data.status)

            default:
                throw new ApiError("データベースのアップデートに失敗しました。", res.data.status)
        }

    } catch (error) {
        throw error
    }
}

const checkDatabaseResultValues = {
    NO_DATABASE: "NO_DATABASE",
    READY: "READY"
} as const
export type checkDatabaseResult = typeof checkDatabaseResultValues[keyof typeof checkDatabaseResultValues];
/**
 * checkDatabase
 */
export const checkDatabase = async (config?: AxiosRequestConfig): Promise<checkDatabaseResult> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/check_database',
            undefined, config
        )
        if (!Object.keys(checkDatabaseResultValues).includes(res.data.status)) {
            throw new ApiError("internal error", res.data.status)
        }
        return res.data.status
    } catch (error) {
        throw error
    }
}

const checkInitDataResultValues = {
    NO_DATA: "NO_DATA",
    NO_DATABASE: "NO_DATABASE",
    READY: "READY"
} as const
export type checkInitDataResult = typeof checkInitDataResultValues[keyof typeof checkInitDataResultValues];
/**
 * checkInitData
 */
export const checkInitData = async (config?: AxiosRequestConfig): Promise<checkInitDataResult> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/check_initdata',
            undefined, config
        )
        if (!Object.keys(checkInitDataResultValues).includes(res.data.status)) {
            throw new ApiError("internal error", res.data.status)
        }
        return res.data.status
    } catch (error) {
        throw error
    }
}

const checkSystemAdminResultValues = {
    NO_SYSTEMADMIN: "NO_SYSTEMADMIN",
    NO_DATA: "NO_DATA",
    NO_DATABASE: "NO_DATABASE",
    READY: "READY"
} as const
export type checkSystemAdminResult = typeof checkSystemAdminResultValues[keyof typeof checkSystemAdminResultValues];
/**
 * checkSystemAdmin
 */
export const checkSystemAdmin = async (config?: AxiosRequestConfig): Promise<checkSystemAdminResult> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/check_system_admin',
            undefined, config
        )
        if (!Object.keys(checkSystemAdminResultValues).includes(res.data.status)) {
            throw new ApiError("internal error", res.data.status)
        }
        return res.data.status
    } catch (error) {
        throw error
    }
}

const createDatabaseResultValues = {
    ALREADY_EXISTS: "ALREADY_EXISTS",
    SUCCESS: "SUCCESS"
} as const
export type createDatabaseResult = typeof createDatabaseResultValues[keyof typeof createDatabaseResultValues];
/**
 * createDatabase
 */
export const createDatabase = async (config?: AxiosRequestConfig): Promise<createDatabaseResult> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/create_database',
            undefined, config
        )
        if (!Object.keys(createDatabaseResultValues).includes(res.data.status)) {
            throw new ApiError("internal error", res.data.status)
        }
        return res.data.status
    } catch (error) {
        throw error
    }
}

const setInitDataResultValues = {
    ALREADY_INITIALIZED: "ALREADY_INITIALIZED",
    SUCCESS: "SUCCESS"
} as const
export type setInitDataResult = typeof setInitDataResultValues[keyof typeof setInitDataResultValues];
/**
 * createDatabase
 */
export const setInitData = async (config?: AxiosRequestConfig): Promise<setInitDataResult> => {
    try {

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/set_initdata',
            undefined, config
        )
        if (!Object.keys(setInitDataResultValues).includes(res.data.status)) {
            throw new ApiError("internal error", res.data.status)
        }
        return res.data.status
    } catch (error) {
        throw error
    }
}

export interface createSystemAdminProps {
    name: string
    login_str: string
    password: string
}
const createSystemAdminResultValues = {
    NO_DATABASE: "NO_DATABASE",
    NO_DATA: "NO_DATA",
    SUCCESS: "SUCCESS"
} as const
export type createSystemAdminResult = typeof createSystemAdminResultValues[keyof typeof createSystemAdminResultValues];
/**
 * createDatabase
 */
export const createSystemAdmin = async ({
    name,
    login_str,
    password,
}: createSystemAdminProps, config?: AxiosRequestConfig): Promise<createSystemAdminResult> => {
    try {
        let params = new URLSearchParams()
        params.append('name', name)
        params.append('login_str', login_str)
        params.append('password', password)

        let res = await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'system/create_system_admin',
            params, config
        )
        if (!Object.keys(createSystemAdminResultValues).includes(res.data.status)) {
            throw new ApiError("internal error", res.data.status)
        }
        return res.data.status
    } catch (error) {
        throw error
    }
}

export interface getCancelConfListResults {
	equipmentCancelConfList: EquipmentCancelConf[];
	studioCancelConfList: StudioCancelConf[];
}

export const getCancelConfList = async (config?: AxiosRequestConfig): Promise<getCancelConfListResults> => {
	try {
		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "system/get_cancel_conf_list", undefined, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError("サーバーエラー", res.data.status);
		}
		return {
			studioCancelConfList: res.data.list.studio_cancel_conf_list.map((data: any) => ObjectToStudioCancelConf(data)),
			equipmentCancelConfList: res.data.list.equipment_cancel_conf_list.map((data: any) => ObjectToEquipmentCancelConf(data)),
		};
	} catch (error) {
		throw error;
	}
};

export const regEquipmentCancelConf = async (equipmentCancelConf: EquipmentCancelConf, config?: AxiosRequestConfig): Promise<void> => {
	try {
		let params = new URLSearchParams();
		if (equipmentCancelConf.ccfg_start) {
			params.append("ccfg_start", equipmentCancelConf.ccfg_start.toString());
		}
		if (equipmentCancelConf.ccfg_end) {
			params.append("ccfg_end", equipmentCancelConf.ccfg_end.toString());
		}
		if (equipmentCancelConf.ccfg_unit) {
			params.append("ccfg_unit", equipmentCancelConf.ccfg_unit);
		}
		if (equipmentCancelConf.ccfg_raito) {
			params.append("ccfg_raito", equipmentCancelConf.ccfg_raito.toString());
		}

		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "system/reg_equipment_cancel_conf", params, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError("サーバーエラー", res.data.status);
		}
	} catch (error) {
		throw error;
	}
};

export const deleteEquipmentCancelConf = async (equipmentCancelConf: EquipmentCancelConf, config?: AxiosRequestConfig): Promise<void> => {
	try {
		let params = new URLSearchParams();
		if (equipmentCancelConf.ccfg_id) {
			params.append("ccfg_id", equipmentCancelConf.ccfg_id.toString());
		}

		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "system/delete_equipment_cancel_conf", params, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError("サーバーエラー", res.data.status);
		}
	} catch (error) {
		throw error;
	}
};

export const regStudioCancelConf = async (studioCancelConf: StudioCancelConf, config?: AxiosRequestConfig): Promise<void> => {
	try {
		let params = new URLSearchParams();
		if (studioCancelConf.ccfg_start) {
			params.append("ccfg_start", studioCancelConf.ccfg_start.toString());
		}
		if (studioCancelConf.ccfg_end) {
			params.append("ccfg_end", studioCancelConf.ccfg_end.toString());
		}
		if (studioCancelConf.ccfg_unit) {
			params.append("ccfg_unit", studioCancelConf.ccfg_unit);
		}
		if (studioCancelConf.ccfg_raito) {
			params.append("ccfg_raito", studioCancelConf.ccfg_raito.toString());
		}

		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "system/reg_studio_cancel_conf", params, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError("サーバーエラー", res.data.status);
		}
	} catch (error) {
		throw error;
	}
};

export const deleteStudioCancelConf = async (studioCancelConf: StudioCancelConf, config?: AxiosRequestConfig): Promise<void> => {
	try {
		let params = new URLSearchParams();
		if (studioCancelConf.ccfg_id) {
			params.append("ccfg_id", studioCancelConf.ccfg_id.toString());
		}

		let res = await axios.post(process.env.REACT_APP_API_ENDPOINT + "system/delete_studio_cancel_conf", params, config);
		if (res.data.status !== "SUCCESS") {
			throw new ApiError("サーバーエラー", res.data.status);
		}
	} catch (error) {
		throw error;
	}
};